import {IconButton, Menu, MenuItem, Typography} from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import LoopIcon from "@mui/icons-material/Loop"
import styled, {keyframes} from "styled-components"
import React, {useState} from "react"

import {api, Customer} from "@indebted/api"
import {useAgent} from "@indebted/hooks/Agent"
import {useNotification} from "@indebted/components/Notification"
import {ConfirmationDialog} from "@indebted/components/ConfirmationDialog"

function CustomerMenu({customer, reload}: {customer: Customer; reload: () => void}) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined | null>()
	const [MenuIcon, setMenuIcon] = useState<React.ReactNode>(<MoreVertIcon />)
	const [agentEmail] = useAgent()
	const {notification} = useNotification()
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
	const [confirmationDialogOnOKFunc, setConfirmationDialogOnOKFunc] = useState<(() => void) | undefined>(undefined)
	const [confirmationDialogDescription, setConfirmationDialogDescription] = useState("")

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget)
	}

	const handleMenuClose = () => {
		setAnchorEl(null)
	}

	const handleCommsClick = (actionID: string, channel: string, labelAction: string) => {
		handleMenuClose()
		setMenuIcon(<LoadingIcon />)
		api.customer
			.changeComms(customer.ID, {
				actionID,
				channel,
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(`Customer ${labelAction} successful.`)
				setTimeout(reload, 500)
			})
			.catch((error) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})
	}

	const handleDigitalCommunicationConsentClick = ({ID, Display}: {ID: string; Display: string}) => {
		handleMenuClose()
		setMenuIcon(<LoadingIcon />)
		api.customer
			.changeDigitalCommConsent(customer.ID, {
				actionID: ID,
				AgentEmail: agentEmail,
			})
			.then(() => {
				setMenuIcon(<MoreVertIcon />)
				notification.success(`Customer digital comm consent was ${Display} successfully.`)
				setTimeout(reload, 500)
			})
			.catch((error) => {
				setMenuIcon(<MoreVertIcon />)
				notification.error(error.Message)
			})
	}

	const confirmDialog = (description: string, onOK: () => void) => {
		handleMenuClose()
		setConfirmationDialogDescription(description)
		setConfirmationDialogOnOKFunc(() => onOK)
		setConfirmationDialogOpen(true)
	}

	return (
		<>
			<ConfirmationDialog
				description={confirmationDialogDescription}
				isOpen={confirmationDialogOpen}
				onCancel={() => {
					setConfirmationDialogOpen(false)
				}}
				onOK={() => {
					confirmationDialogOnOKFunc?.()
					setConfirmationDialogOpen(false)
				}}
			/>

			<IconButton size="small" onClick={handleClick}>
				{MenuIcon}
			</IconButton>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				anchorOrigin={{vertical: "bottom", horizontal: "right"}}
				transformOrigin={{vertical: "top", horizontal: "right"}}
				onClose={handleMenuClose}
			>
				{customer.CommsOptions &&
					customer.CommsOptions.map((option, key) => {
						return [
							<MenuDivider key={key} color="textSecondary" variant="caption">
								{option.Label}
							</MenuDivider>,
							option.Items.map((item) => (
								<MenuItem
									key={item.ID}
									onClick={() =>
										confirmDialog(`${option.Label} ${item.Display} selected. Do you confirm?`, () =>
											handleCommsClick(option.ID, item.ID, option.Label),
										)
									}
								>
									{item.Display}
								</MenuItem>
							)),
						]
					})}
				{customer.DigitalCommunicationConsentOptions &&
					customer.DigitalCommunicationConsentOptions.map((option, key) => {
						return [
							<MenuDivider key={key} color="textSecondary" variant="caption">
								{option.Label}
							</MenuDivider>,
							option.Items.map((item) => (
								<MenuItem
									key={item.ID}
									onClick={() =>
										confirmDialog(`${option.Label} ${item.Display} selected. Do you confirm?`, () =>
											handleDigitalCommunicationConsentClick(item),
										)
									}
								>
									{item.Selected && <CheckIcon fontSize="small" />}
									{item.Display}
								</MenuItem>
							)),
						]
					})}
			</Menu>
		</>
	)
}

const loadingIconAnimations = keyframes`
	from { transform:rotate(0deg); }
    to { transform:rotate(-360deg);}
`

const LoadingIcon = styled(LoopIcon)`
	animation: ${loadingIconAnimations} 4s infinite linear;
`

const MenuDivider = styled(Typography)`
	padding-left: 6px;
`

export {CustomerMenu}
